/* eslint-disable react-refresh/only-export-components */
import { createContext, useCallback, useContext, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { cobranzasService } from '@/services/api/cobranzas.service';
import { archivosService } from '@/services/api/archivos.service';
import { toastBridge } from '@/services/api/toast-bridge';
import { useCurrentCompanyId, useCurrentEstablecimientoId } from '@/contexts/UserSessionContext';
import type { CuentaPorCobrarSummary, CobranzaDocumento, RegistrarCobranzaInput, RegistrarCobranzaMultipleInput } from '@/modelos/cobranzas/cobranzas.types';
import { DEFAULT_COBRANZA_FILTERS } from '@/utils/cobranzas/constants';

interface RefreshCobranzasOpts {
  fechaDesde?: string;
  fechaHasta?: string;
}

interface CobranzasContextValue {
  cuentas: CuentaPorCobrarSummary[];
  cobranzas: CobranzaDocumento[];
  esCargando: boolean;
  registerCobranza: (input: RegistrarCobranzaInput) => Promise<CobranzaDocumento>;
  registerCobranzaMultiple: (input: RegistrarCobranzaMultipleInput) => Promise<CobranzaDocumento>;
  upsertCuenta: (cuenta: CuentaPorCobrarSummary) => void;
  refreshCobranzas: (opts?: RefreshCobranzasOpts) => Promise<void>;
}

const CobranzasContext = createContext<CobranzasContextValue | undefined>(undefined);

async function subirAdjuntosCobranza(cobranzaId: string, files: File[] | undefined, logPrefix: string) {
  if (!files || files.length === 0) return;
  console.log(`[${logPrefix}] Subiendo`, files.length, 'adjunto(s) para cobranza', cobranzaId);
  const results = await Promise.allSettled(
    files.map((file) => archivosService.subirParaCobranza(cobranzaId, file)),
  );
  const failures = results.filter((r): r is PromiseRejectedResult => r.status === 'rejected');
  if (failures.length > 0) {
    const reason = failures[0].reason instanceof Error ? failures[0].reason.message : String(failures[0].reason);
    console.error(`[${logPrefix}] Error subiendo adjuntos:`, reason, '— cobranzaId:', cobranzaId);
    toastBridge.error(`No se pudieron subir ${failures.length} adjunto(s): ${reason}`, 'upload-cobranza-error');
  }
}

export function CobranzasProvider({ children }: { children: ReactNode }) {
  const empresaId = useCurrentCompanyId();
  const establecimientoId = useCurrentEstablecimientoId();
  const [cuentas, setCuentas] = useState<CuentaPorCobrarSummary[]>([]);
  const [cobranzas, setCobranzas] = useState<CobranzaDocumento[]>([]);
  const esCargando = false;
  const currentDateRangeRef = useRef<RefreshCobranzasOpts>({
    fechaDesde: DEFAULT_COBRANZA_FILTERS.rangoFechas.from,
    fechaHasta: DEFAULT_COBRANZA_FILTERS.rangoFechas.to,
  });

  const upsertCuenta = useCallback((cuenta: CuentaPorCobrarSummary) => {
    setCuentas((prev) => {
      const index = prev.findIndex((c) => c.comprobanteId === cuenta.comprobanteId);
      if (index >= 0) {
        const updated = [...prev];
        updated[index] = cuenta;
        return updated;
      }
      return [cuenta, ...prev];
    });
  }, []);

  const registerCobranza = useCallback(
    async (input: RegistrarCobranzaInput): Promise<CobranzaDocumento> => {
      const dto = cobranzasService.buildInputDto(input);
      const res = await cobranzasService.create(dto as Parameters<typeof cobranzasService.create>[0]);

      if (!res.ok || !res.data) {
        throw new Error((res as { mensaje?: string }).mensaje ?? 'No se pudo registrar la cobranza');
      }

      const newCobranza = cobranzasService.mapCobranzaDto(res.data);
      await subirAdjuntosCobranza(newCobranza.id, input.payload.attachmentFiles, 'CobranzasContext');

      setCobranzas((prev) => [newCobranza, ...prev]);

      const refreshedCuentasRes = await cobranzasService.getCuentas({
        empresaId: empresaId ?? undefined,
        establecimientoId: establecimientoId ?? undefined,
      });
      if (refreshedCuentasRes.ok && Array.isArray(refreshedCuentasRes.data)) {
        setCuentas(refreshedCuentasRes.data.map(cobranzasService.mapCuentaDto));
      }

      return newCobranza;
    },
    [empresaId, establecimientoId],
  );

  const registerCobranzaMultiple = useCallback(
    async (input: RegistrarCobranzaMultipleInput): Promise<CobranzaDocumento> => {
      const dto = cobranzasService.buildInputDtoMultiple(input);
      const res = await cobranzasService.create(dto as Parameters<typeof cobranzasService.create>[0]);

      if (!res.ok || !res.data) {
        throw new Error((res as { mensaje?: string }).mensaje ?? 'No se pudo registrar la cobranza');
      }

      const newCobranza = cobranzasService.mapCobranzaDto(res.data);
      await subirAdjuntosCobranza(newCobranza.id, input.payload.attachmentFiles, 'CobranzasContext');

      setCobranzas((prev) => [newCobranza, ...prev]);

      const refreshedCuentasRes = await cobranzasService.getCuentas({
        empresaId: empresaId ?? undefined,
        establecimientoId: establecimientoId ?? undefined,
      });
      if (refreshedCuentasRes.ok && Array.isArray(refreshedCuentasRes.data)) {
        setCuentas(refreshedCuentasRes.data.map(cobranzasService.mapCuentaDto));
      }

      return newCobranza;
    },
    [empresaId, establecimientoId],
  );

  const refreshCobranzas = useCallback(async (opts?: RefreshCobranzasOpts) => {
    if (!empresaId) return;
    const defaults = DEFAULT_COBRANZA_FILTERS.rangoFechas;
    const fechaDesde = opts?.fechaDesde ?? defaults.from;
    const fechaHasta = opts?.fechaHasta ?? defaults.to;

    currentDateRangeRef.current = { fechaDesde, fechaHasta };

    try {
      const res = await cobranzasService.getAll({
        empresaId,
        establecimientoId: establecimientoId ?? undefined,
        fechaDesde,
        fechaHasta,
      });
      if (res.ok && Array.isArray(res.data)) {
        setCobranzas(res.data.map(cobranzasService.mapCobranzaDto));
      }
    } catch (err) {
      console.error('[CobranzasContext] refreshCobranzas:', err);
    }
  }, [empresaId, establecimientoId]);

  return (
    <CobranzasContext.Provider value={{ cuentas, cobranzas, esCargando, registerCobranza, registerCobranzaMultiple, upsertCuenta, refreshCobranzas }}>
      {children}
    </CobranzasContext.Provider>
  );
}

async function registerCobranzaFallback(input: RegistrarCobranzaInput): Promise<CobranzaDocumento> {
  const dto = cobranzasService.buildInputDto(input);
  const res = await cobranzasService.create(dto as Parameters<typeof cobranzasService.create>[0]);
  if (!res.ok || !res.data) {
    throw new Error((res as { mensaje?: string }).mensaje ?? 'No se pudo registrar la cobranza');
  }
  const cobranza = cobranzasService.mapCobranzaDto(res.data);
  await subirAdjuntosCobranza(cobranza.id, input.payload.attachmentFiles, 'CobranzasContext fallback');
  return cobranza;
}

async function registerCobranzaMultipleFallback(input: RegistrarCobranzaMultipleInput): Promise<CobranzaDocumento> {
  const dto = cobranzasService.buildInputDtoMultiple(input);
  const res = await cobranzasService.create(dto as Parameters<typeof cobranzasService.create>[0]);
  if (!res.ok || !res.data) {
    throw new Error((res as { mensaje?: string }).mensaje ?? 'No se pudo registrar la cobranza');
  }
  const cobranza = cobranzasService.mapCobranzaDto(res.data);
  await subirAdjuntosCobranza(cobranza.id, input.payload.attachmentFiles, 'CobranzasContext fallback');
  return cobranza;
}

export function useCobranzasContext(): CobranzasContextValue {
  const ctx = useContext(CobranzasContext);
  if (!ctx) {
    return {
      cuentas: [],
      cobranzas: [],
      esCargando: false,
      registerCobranza: registerCobranzaFallback,
      registerCobranzaMultiple: registerCobranzaMultipleFallback,
      upsertCuenta: () => {},
      refreshCobranzas: () => Promise.resolve(),
    };
  }
  return ctx;
}

export { CobranzasContext };
